import { GrTypography } from "@jnpmedi/grebob";
import { GrStack } from "@jnpmedi/grebob/dist/components/layout/GrStack";
import { MvnMessage } from "@newLibs/utils";
import { defaultUuid } from "@newHooks/context/document/document.context";
import { DocumentMarker } from "@newHooks/context/document/documentMarker.context";
import { EConsentSignerSpec } from "@newHooks/context/eConsent/eConsent.context";
import { ESignSigner } from "@newHooks/context/eSign/eSignSigner.context";
import { notify } from "@newHooks/context/snackbar/notify";
import { DOCS_DOCUMENT_SIGN_LANG } from "@maven-msg/lang/docs";
import { DocumentSignerAttr, DocumentSignerAttrTarget, DocumentSignerAttrType, DocumentSignerReq } from "@maven-rest/docs/DocumentSign.dto";
import { TypeHelper } from "@maven-helper/helpers/Type.helper";

export type ESignSignerSpec = DocumentSignerReq & { uuid: string };

/**
 * NOTE: e-Sign, e-Consent에서 본인인증, e-mail 인증 이외에 인증 불필요 옵션이 새롭게 추가되면서
 * 해당 경우(인증 불필요) 에 사용될 값을 FE 측에서만 사용하기 위해 정의된 상수
 * 실제 해당 속성의 값이 BE측으로 전달되지 않습니다. (전달되면 안됩니다. DB 칼럼의 enum 유형에 없는 타입입니다.)
 *
 * DocumentSignerAttrType enum에는 정의하지 않았음.
 * */
export const DOCUMENT_SIGNER_NO_VERIFY_KEY = "NON-VERIFY";

export const UserVerificationOptionList = [
  DOCUMENT_SIGNER_NO_VERIFY_KEY,
  DocumentSignerAttrType.RequireEmailVerify,
  DocumentSignerAttrType.RequireIdentityVerify,
];

export const UserVerificationOptionListConst = [
  DOCUMENT_SIGNER_NO_VERIFY_KEY,
  DocumentSignerAttrType.RequireEmailVerify,
  DocumentSignerAttrType.RequireIdentityVerify,
] as const;

export type VerifyAttributeType = typeof UserVerificationOptionListConst[number];

export const hasVerifyAttribute = (attrs: DocumentSignerAttr[]): boolean => {
  return attrs.some((attribute) =>
    [DocumentSignerAttrType.RequireIdentityVerify, DocumentSignerAttrType.RequireEmailVerify].includes(attribute.attr)
  );
};

export const getSignerVerifyAttribute = (signer: EConsentSignerSpec | ESignSigner): DocumentSignerAttrType | "NON-VERIFY" => {
  const find = signer.attrs.find((attr) => UserVerificationOptionList.includes(attr.attr));
  return find ? find.attr : DOCUMENT_SIGNER_NO_VERIFY_KEY;
};

// NOTE: Email인증 attr를 추가하려는 signer와 맵핑되는 그룹의 marker의 기본 Uuid들을 사용하되, DocumentContext의 defaultUuid사용
export const makeBaseEmailVerifyAttr = (marker?: DocumentMarker, verifyType?: VerifyAttributeType): DocumentSignerAttr => {
  return {
    target: DocumentSignerAttrTarget.Signer,
    attr: TypeHelper.isNullishType(verifyType) ? DocumentSignerAttrType.RequireEmailVerify : verifyType,
    value: "true",
    groupUuid: marker?.groupUuid || defaultUuid,
    attrUuid: marker?.attrUuid || defaultUuid,
    pageIndex: null,
    coordX: null,
    coordY: null,
  } as DocumentSignerAttr;
};

// NOTE: GxP 규정 준수 체크박스가 선택됐지만, 이메일 인증이 선택이 안된경우에 e-sign | e-consent 요청하지 못하도록 방어
export const isGxpCheckAndDoEmailVerify = (signers: Array<ESignSignerSpec | EConsentSignerSpec>): boolean => {
  if (signers.some((signer) => !hasVerifyAttribute(signer.attrs))) {
    notify({
      action: (
        <GrStack flexDirection="column" gap={4}>
          <GrTypography type="Content.Body03">{MvnMessage(DOCS_DOCUMENT_SIGN_LANG.DOCUMENT_GXP_EMAIL_VERIFY_REQUIRED_TITLE)}</GrTypography>
          <GrTypography type="Content.Caption03">
            {MvnMessage(DOCS_DOCUMENT_SIGN_LANG.DOCUMENT_GXP_EMAIL_VERIFY_REQUIRED_DESC)}
          </GrTypography>
        </GrStack>
      ),
      severity: "warning",
    });
    return false;
  }
  return true;
};
